import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { Link, useNavigate } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { PolicyStatusBadge } from './PolicyStatusBadge';
import type { Policy } from '@/hooks/usePolicies';
import { ChevronLeft, ChevronRight, Archive, CheckCircle, AlertTriangle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useAckStats, type PolicyAckStats } from '@/hooks/useAckStats';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';

interface PolicyTableProps {
  policies: Policy[];
  ackStatusMap?: Map<string, boolean>;
  composerMode?: 'update' | 'draft';
}

type ReviewStatus = 'archived' | 'overdue' | 'due_soon' | 'needs_ack' | 'current';

function getReviewStatus(
  policy: Policy,
  ackStatusMap?: Map<string, boolean>
): ReviewStatus {
  if (policy.is_archived) {
    return 'archived';
  }

  const todayStr = new Date().toISOString().split('T')[0];
  const thirtyDays = new Date();
  thirtyDays.setDate(thirtyDays.getDate() + 30);
  const thirtyDaysStr = thirtyDays.toISOString().split('T')[0];
  
  if (!policy.next_review_date) {
    return 'current';
  }
  
  if (policy.next_review_date < todayStr && policy.status === 'Approved') {
    return 'overdue';
  }
  
  if (policy.next_review_date >= todayStr && policy.next_review_date <= thirtyDaysStr && policy.status === 'Approved') {
    return 'due_soon';
  }
  
  if (ackStatusMap && policy.status === 'Approved') {
    const isAcknowledged = ackStatusMap.get(policy.id) ?? true;
    if (!isAcknowledged) {
      return 'needs_ack';
    }
  }
  
  return 'current';
}

function ReviewStatusBadge({ status, policyId, ackStats }: { status: ReviewStatus; policyId: string; ackStats?: PolicyAckStats }) {
  if (status === 'archived') {
    return (
      <span className="text-sm text-muted-foreground">—</span>
    );
  }

  if (status === 'current') {
    return (
      <span className="flex items-center gap-1.5 text-status-approved text-sm font-medium">
        <CheckCircle className="h-3.5 w-3.5" />
        Current
      </span>
    );
  }

  if (status === 'needs_ack' && ackStats) {
    if (ackStats.isFullyAcknowledged) {
      return (
        <span className="flex items-center gap-1.5 text-status-approved text-sm font-medium">
          <CheckCircle className="h-3.5 w-3.5" />
          Current
        </span>
      );
    }

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Link to={`/policies/${policyId}?tab=acknowledgements`}>
            <div className="flex items-center gap-2 cursor-pointer">
              <div className="h-2 w-2 rounded-full bg-status-acknowledgment" />
              <span className="text-sm font-medium text-status-acknowledgment hover:underline">
                Needs Ack ({ackStats.pendingCount}/{ackStats.totalStaff})
              </span>
            </div>
          </Link>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-[250px]">
          <p className="font-medium text-xs">Pending: {ackStats.pendingNames.join(', ')}</p>
        </TooltipContent>
      </Tooltip>
    );
  }
  
  const config = {
    overdue: {
      color: 'bg-status-overdue',
      textColor: 'text-status-overdue',
      label: 'Over Due',
    },
    due_soon: {
      color: 'bg-status-due-soon',
      textColor: 'text-status-due-soon',
      label: 'Due Soon',
    },
    needs_ack: {
      color: 'bg-status-acknowledgment',
      textColor: 'text-status-acknowledgment',
      label: 'Needs Ack',
    },
  }[status];

  if (!config) return null;
  
  return (
    <div className="flex items-center gap-2">
      <div className={cn("h-2 w-2 rounded-full", config.color)} />
      <span className={cn("text-sm font-medium", config.textColor)}>
        {config.label}
      </span>
    </div>
  );
}

function getPageNumbers(currentPage: number, totalPages: number): (number | 'ellipsis')[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  const pages: (number | 'ellipsis')[] = [1];
  if (currentPage > 3) pages.push('ellipsis');
  const start = Math.max(2, currentPage - 1);
  const end = Math.min(totalPages - 1, currentPage + 1);
  for (let i = start; i <= end; i++) pages.push(i);
  if (currentPage < totalPages - 2) pages.push('ellipsis');
  pages.push(totalPages);
  return pages;
}

export function PolicyTable({ policies, ackStatusMap, composerMode }: PolicyTableProps) {
  const { data: ackStatsMap } = useAckStats();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Reset page when policies change
  useEffect(() => {
    setCurrentPage(1);
  }, [policies.length]);


  // Pagination
  const totalPages = Math.max(1, Math.ceil(policies.length / pageSize));
  const startIdx = (currentPage - 1) * pageSize;
  const endIdx = Math.min(startIdx + pageSize, policies.length);
  const paginatedPolicies = policies.slice(startIdx, endIdx);

  if (policies.length === 0) {
    return (
      <div className="rounded-[8px] border border-border bg-card p-8 text-center text-muted-foreground">
        No policies found matching your filters.
      </div>
    );
  }

  const pageNumbers = getPageNumbers(currentPage, totalPages);

  return (
    <TooltipProvider>
      <div className="rounded-[8px] border border-border bg-card overflow-hidden">
            <Table>
              <TableHeader className="bg-[hsl(var(--table-header))]">
                <TableRow>
                  <TableHead className="w-[280px]">Policy Name</TableHead>
                  <TableHead className="hidden md:table-cell">Category</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Review Status</TableHead>
                  <TableHead>Ack Status</TableHead>
                  <TableHead className="hidden md:table-cell">Assigned To</TableHead>
                  <TableHead className="hidden md:table-cell">Next Review</TableHead>
                  <TableHead className="w-[40px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedPolicies.map((policy) => {
                  const reviewStatus = getReviewStatus(policy, ackStatusMap);
                  const ackStats = ackStatsMap?.get(policy.id);
                  
                  return (
                    <TableRow 
                      key={policy.id} 
                      className={cn(
                        "group hover:bg-muted/50",
                        policy.is_archived && "bg-muted/50"
                      )}
                    >
                      <TableCell className="font-medium">
                        {composerMode ? (
                          <button
                            onClick={() => {
                              const target = composerMode === 'update'
                                ? `/composer?policy_id=${policy.id}&mode=update`
                                : `/composer?policy_id=${policy.id}`;
                              navigate(target);
                            }}
                            className="hover:underline text-left"
                          >
                            {policy.name}
                          </button>
                        ) : (
                          <Link
                            to={policy.status === 'draft' ? `/composer?policy_id=${policy.id}` : `/policies/${policy.id}`}
                            className="hover:underline"
                          >
                            {policy.name}
                          </Link>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground hidden md:table-cell">
                        {policy.category}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <PolicyStatusBadge status={policy.status} />
                          {policy.is_archived && (
                            <Badge
                              variant="outline"
                              className="gap-1 text-muted-foreground border-muted-foreground/30"
                            >
                              <Archive className="h-3 w-3" />
                              Archived
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <ReviewStatusBadge status={reviewStatus} policyId={policy.id} ackStats={ackStats} />
                      </TableCell>
                      <TableCell>
                        <AckStatusCell policyId={policy.id} policy={policy} ackStats={ackStats} />
                      </TableCell>
                      <TableCell className="text-muted-foreground hidden md:table-cell">
                        {policy.owner_profile?.full_name ?? '—'}
                      </TableCell>
                      <TableCell className="text-muted-foreground hidden md:table-cell">
                        {format(new Date(policy.next_review_date), 'MMM d, yyyy')}
                      </TableCell>
                      <TableCell>
                        {composerMode ? (
                          <Button
                            size="sm"
                            className="opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={() => {
                              const target = composerMode === 'update'
                                ? `/composer?policy_id=${policy.id}&mode=update`
                                : `/composer?policy_id=${policy.id}`;
                              navigate(target);
                            }}
                          >
                            Open in Policy Builder
                          </Button>
                        ) : (
                          <Link to={policy.status === 'draft' ? `/composer?policy_id=${policy.id}` : `/policies/${policy.id}`}>
                            <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
                          </Link>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
      </div>

      {/* Pagination bar */}
      <div className="flex items-center justify-between px-4 py-3 text-sm text-muted-foreground">
        <span>{startIdx + 1}–{endIdx} of {policies.length}</span>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="gap-1 text-muted-foreground"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((p) => p - 1)}
          >
            <ChevronLeft className="h-4 w-4" />
            Prev
          </Button>
          {pageNumbers.map((p, i) =>
            p === 'ellipsis' ? (
              <span key={`e${i}`} className="px-1">…</span>
            ) : (
              <button
                key={p}
                onClick={() => setCurrentPage(p)}
                className={cn(
                  "h-8 w-8 rounded text-sm font-medium",
                  p === currentPage
                    ? "bg-teal text-white"
                    : "hover:bg-muted text-muted-foreground"
                )}
              >
                {p}
              </button>
            )
          )}
          <Button
            variant="ghost"
            size="sm"
            className="gap-1 text-muted-foreground"
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage((p) => p + 1)}
          >
            Next
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <Select
          value={String(pageSize)}
          onValueChange={(v) => {
            setPageSize(Number(v));
            setCurrentPage(1);
          }}
        >
          <SelectTrigger className="w-[110px] h-8 text-muted-foreground">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="10">10 per page</SelectItem>
            <SelectItem value="25">25 per page</SelectItem>
            <SelectItem value="50">50 per page</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </TooltipProvider>
  );
}

function AckStatusCell({ policyId, policy, ackStats }: { policyId: string; policy: Policy; ackStats?: PolicyAckStats }) {
  if (!policy.requires_acknowledgement || policy.status !== 'Approved' || policy.is_archived) {
    return <span className="text-sm text-muted-foreground">—</span>;
  }

  if (!ackStats) {
    return <span className="text-sm text-muted-foreground">—</span>;
  }

  if (ackStats.isFullyAcknowledged) {
    return (
      <span className="flex items-center gap-1.5 text-status-approved text-sm font-medium">
        <CheckCircle className="h-3.5 w-3.5" />
        Acknowledged
      </span>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Link to={`/policies/${policyId}?tab=acknowledgements`}>
          <Badge
            variant="outline"
            className="gap-1.5 font-medium border-primary/30 bg-primary/10 text-primary cursor-pointer hover:bg-primary/20 transition-colors"
          >
            <AlertTriangle className="h-3 w-3" />
            Needs Ack ({ackStats.pendingCount}/{ackStats.totalStaff})
          </Badge>
        </Link>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="max-w-[250px]">
        <p className="font-medium text-xs">Pending: {ackStats.pendingNames.join(', ')}</p>
      </TooltipContent>
    </Tooltip>
  );
}
